import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Save,
  Refresh,
  Settings,
  Add,
  Delete,
  Edit,
  Phone,
  Security,
  Chat
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useSettings, AppSettings } from '../contexts/SettingsContext';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`report-settings-tabpanel-${index}`}
      aria-labelledby={`report-settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ReportSettings: React.FC = () => {
  const { hasRole } = useAuth();
  const { settings, updateSetting, resetSettings, saveSettings } = useSettings();
  const { mode } = useCustomTheme();
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'warning' | 'info' });
  const [isCustomFieldDialogOpen, setIsCustomFieldDialogOpen] = useState(false);
  const [editingCustomField, setEditingCustomField] = useState<any>(null);
  const [currentReportType, setCurrentReportType] = useState<'call' | 'risk' | 'livechat'>('call');

  // Rol kontrolü - sadece Admin erişebilir
  if (!hasRole(['admin'])) {
    return (
      <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
        <Alert severity="error" sx={{ mt: 4 }}>
          Bu sayfaya erişim yetkiniz bulunmamaktadır. Sadece Admin kullanıcıları bu sayfayı görüntüleyebilir.
        </Alert>
      </Container>
    );
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSettingChange = (path: string, value: any) => {
    updateSetting(path, value);
  };

  const handleSaveSettings = () => {
    saveSettings();
    setSnackbar({
      open: true,
      message: 'Rapor ayarları başarıyla kaydedildi!',
      severity: 'success'
    });
  };

  const handleResetSettings = () => {
    resetSettings();
    setSnackbar({
      open: true,
      message: 'Rapor ayarları varsayılan değerlere sıfırlandı!',
      severity: 'info'
    });
  };

  const handleAddCustomField = (reportType: 'call' | 'risk' | 'livechat') => {
    setCurrentReportType(reportType);
    setEditingCustomField(null);
    setIsCustomFieldDialogOpen(true);
  };

  const handleEditCustomField = (reportType: 'call' | 'risk' | 'livechat', field: any) => {
    setCurrentReportType(reportType);
    setEditingCustomField(field);
    setIsCustomFieldDialogOpen(true);
  };

  const handleDeleteCustomField = (reportType: 'call' | 'risk' | 'livechat', index: number) => {
    const currentSettings = settings[`${reportType}ReportSettings` as keyof AppSettings] as any;
    const updatedCustomFields = currentSettings.customFields.filter((_: any, i: number) => i !== index);
    handleSettingChange(`${reportType}ReportSettings.customFields`, updatedCustomFields);
  };

  const handleSaveCustomField = (fieldData: any) => {
    const currentSettings = settings[`${currentReportType}ReportSettings` as keyof AppSettings] as any;
    let updatedCustomFields;
    
    if (editingCustomField) {
      // Düzenleme
      updatedCustomFields = currentSettings.customFields.map((field: any, index: number) => 
        index === editingCustomField.index ? fieldData : field
      );
    } else {
      // Yeni ekleme
      updatedCustomFields = [...currentSettings.customFields, fieldData];
    }
    
    handleSettingChange(`${currentReportType}ReportSettings.customFields`, updatedCustomFields);
    setIsCustomFieldDialogOpen(false);
    setEditingCustomField(null);
  };

  const getReportTypeSettings = (type: 'call' | 'risk' | 'livechat') => {
    return settings[`${type}ReportSettings` as keyof AppSettings] as any;
  };

  const getReportTypeTitle = (type: 'call' | 'risk' | 'livechat') => {
    const titles = {
      call: 'Call Raporu',
      risk: 'Risk Raporu',
      livechat: 'Canlı Chat Raporu'
    };
    return titles[type];
  };

  const getReportTypeIcon = (type: 'call' | 'risk' | 'livechat') => {
    const icons = {
      call: <Phone />,
      risk: <Security />,
      livechat: <Chat />
    };
    return icons[type];
  };

  const renderReportTypeSettings = (type: 'call' | 'risk' | 'livechat') => {
    const reportSettings = getReportTypeSettings(type);
    
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 3 }}>
        {/* Genel Ayarlar */}
        <Card sx={{ p: 3, backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'white' }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getReportTypeIcon(type)}
            {getReportTypeTitle(type)} - Genel Ayarlar
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <FormControlLabel
            control={
              <Switch
                checked={reportSettings.enabled}
                onChange={(e) => handleSettingChange(`${type}ReportSettings.enabled`, e.target.checked)}
              />
            }
            label="Rapor Türü Aktif"
          />
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Zorunlu Alanlar
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={reportSettings.requiredFields.join(', ')}
              onChange={(e) => handleSettingChange(`${type}ReportSettings.requiredFields`, e.target.value.split(',').map(f => f.trim()).filter(f => f))}
              placeholder="Alanları virgülle ayırın"
              size="small"
            />
          </Box>
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Opsiyonel Alanlar
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={reportSettings.optionalFields.join(', ')}
              onChange={(e) => handleSettingChange(`${type}ReportSettings.optionalFields`, e.target.value.split(',').map(f => f.trim()).filter(f => f))}
              placeholder="Alanları virgülle ayırın"
              size="small"
            />
          </Box>
        </Card>

        {/* Özel Alanlar */}
        <Card sx={{ p: 3, backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'white' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Özel Alanlar
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleAddCustomField(type)}
              size="small"
            >
              Alan Ekle
            </Button>
          </Box>
          <Divider sx={{ mb: 2 }} />
          
          <List>
            {reportSettings.customFields.map((field: any, index: number) => (
              <ListItem key={index} sx={{ px: 0 }}>
                <ListItemText
                  primary={field.name}
                  secondary={`Tip: ${field.type} | Zorunlu: ${field.required ? 'Evet' : 'Hayır'}`}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => handleEditCustomField(type, { ...field, index })}
                    size="small"
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    edge="end"
                    onClick={() => handleDeleteCustomField(type, index)}
                    size="small"
                    color="error"
                  >
                    <Delete />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
            {reportSettings.customFields.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                Henüz özel alan eklenmemiş
              </Typography>
            )}
          </List>
        </Card>
      </Box>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Settings />
          Rapor Ayarları
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleResetSettings}
          >
            Sıfırla
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSaveSettings}
          >
            Kaydet
          </Button>
        </Box>
      </Box>

      <Card sx={{ backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'white' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab 
              label="Call Raporu" 
              icon={<Phone />} 
              iconPosition="start"
            />
            <Tab 
              label="Risk Raporu" 
              icon={<Security />} 
              iconPosition="start"
            />
            <Tab 
              label="Canlı Chat Raporu" 
              icon={<Chat />} 
              iconPosition="start"
            />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          {renderReportTypeSettings('call')}
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          {renderReportTypeSettings('risk')}
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          {renderReportTypeSettings('livechat')}
        </TabPanel>
      </Card>

      {/* Özel Alan Ekleme/Düzenleme Dialog'u */}
      <Dialog 
        open={isCustomFieldDialogOpen} 
        onClose={() => setIsCustomFieldDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingCustomField ? 'Özel Alan Düzenle' : 'Yeni Özel Alan Ekle'}
        </DialogTitle>
        <DialogContent>
          <CustomFieldForm
            field={editingCustomField}
            onSave={handleSaveCustomField}
            onCancel={() => setIsCustomFieldDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

// Özel Alan Formu Komponenti
const CustomFieldForm: React.FC<{
  field: any;
  onSave: (fieldData: any) => void;
  onCancel: () => void;
}> = ({ field, onSave, onCancel }) => {
  const [fieldData, setFieldData] = useState({
    name: field?.name || '',
    type: field?.type || 'text',
    required: field?.required || false
  });

  const handleSave = () => {
    if (!fieldData.name.trim()) return;
    onSave(fieldData);
  };

  return (
    <Box sx={{ pt: 2 }}>
      <TextField
        fullWidth
        label="Alan Adı"
        value={fieldData.name}
        onChange={(e) => setFieldData({ ...fieldData, name: e.target.value })}
        sx={{ mb: 2 }}
      />
      
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Alan Tipi</InputLabel>
        <Select
          value={fieldData.type}
          onChange={(e) => setFieldData({ ...fieldData, type: e.target.value })}
        >
          <MenuItem value="text">Metin</MenuItem>
          <MenuItem value="number">Sayı</MenuItem>
          <MenuItem value="boolean">Evet/Hayır</MenuItem>
          <MenuItem value="rating">Puanlama (1-5)</MenuItem>
          <MenuItem value="select">Seçim Listesi</MenuItem>
          <MenuItem value="date">Tarih</MenuItem>
        </Select>
      </FormControl>
      
      <FormControlLabel
        control={
          <Switch
            checked={fieldData.required}
            onChange={(e) => setFieldData({ ...fieldData, required: e.target.checked })}
          />
        }
        label="Zorunlu Alan"
      />
      
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
        <Button onClick={onCancel}>
          İptal
        </Button>
        <Button variant="contained" onClick={handleSave}>
          Kaydet
        </Button>
      </Box>
    </Box>
  );
};

export default ReportSettings;
